import React from "react";
import PolicyPageLayout, { PolicyList, PolicySection } from "./PolicyPageLayout";
import { COMPANY_DETAILS } from "./policyData";

const PrivacyPolicy = () => {
  return (
    <PolicyPageLayout
      title="Privacy Policy"
      subtitle={`${COMPANY_DETAILS.name} respects your privacy. This policy explains what personal information we collect, how we use it for orders, repairs and customer support, how payment-related data is handled when you pay through PhonePe or other authorised gateways, and how you can contact us regarding your data.`}
    >
      <PolicySection title="1. Scope of This Policy">
        <p>
          This policy applies when you visit {COMPANY_DETAILS.website}, submit a
          form, place an order, request a repair, communicate with our support
          team, or make a payment for goods or services offered by{" "}
          {COMPANY_DETAILS.name}.
        </p>
      </PolicySection>

      <PolicySection title="2. Information We Collect">
        <p>Depending on how you interact with our website, we may collect:</p>
        <PolicyList
          items={[
            "Identity and contact information such as your name, mobile number, email address and delivery address.",
            "Order, repair and support details such as products viewed, products purchased, service requests, invoices, warranty requests and communication history.",
            "Payment-related references such as transaction ID, merchant order ID, payment status, bank reference and gateway response shared by our authorised payment partners.",
            "Technical information such as IP address, browser type, device details, pages visited and approximate location information derived from your connection.",
            "Any information you voluntarily submit through forms, WhatsApp, email, phone calls or support messages.",
          ]}
        />
      </PolicySection>

      <PolicySection title="3. How We Use Your Information">
        <PolicyList
          items={[
            "To create, process, confirm and fulfill your orders or service requests.",
            "To schedule repair work, pickup, delivery, inspection and warranty support.",
            "To verify payments, prevent fraud, handle chargeback or dispute requests and maintain transaction records.",
            "To respond to your enquiries, complaints, return requests and refund requests.",
            "To improve website performance, product listings, service quality and customer experience.",
            "To send essential service updates such as order confirmation, payment status, shipping updates, service approvals and support communication.",
          ]}
        />
      </PolicySection>

      <PolicySection title="4. Payments and PhonePe">
        <p>
          Online payments on our website may be processed through authorised
          third-party payment partners such as PhonePe. We do not store your
          full card number, UPI PIN, CVV, OTP or similar sensitive payment
          credentials on our website or application.
        </p>
        <p>
          When you make a payment, we may receive limited transaction data from
          the payment partner, such as payment reference number, status,
          merchant transaction ID and time of payment, strictly for order
          confirmation, reconciliation, refund handling, fraud prevention and
          dispute response.
        </p>
      </PolicySection>

      <PolicySection title="5. Sharing of Information">
        <p>We may share information only on a need-to-know basis with:</p>
        <PolicyList
          items={[
            "Courier, logistics and delivery partners for shipping and delivery fulfillment.",
            "Payment partners and banking channels for payment processing, settlement, verification and refunds.",
            "Service technicians, warranty partners or suppliers involved in repair, replacement or product support.",
            "Law enforcement, government authorities or regulators where disclosure is required under applicable law.",
          ]}
        />
        <p>
          We do not sell your personal information to third parties for their
          independent marketing use.
        </p>
      </PolicySection>

      <PolicySection title="6. Cookies and Analytics">
        <p>
          We may use cookies or similar technologies to keep you signed in,
          remember preferences, understand website usage patterns and improve
          overall functionality. You can control cookies through your browser
          settings, although disabling them may affect certain site features.
        </p>
      </PolicySection>

      <PolicySection title="7. Data Retention">
        <p>
          We retain personal data only for as long as reasonably necessary for
          order fulfillment, warranty handling, customer support, accounting,
          legal compliance, fraud prevention and dispute resolution. Payment and
          order references may be retained for audit and reconciliation even
          after a transaction is completed.
        </p>
      </PolicySection>

      <PolicySection title="8. Data Security">
        <p>
          We use reasonable administrative, technical and operational safeguards
          to protect personal data against unauthorised access, disclosure,
          alteration and misuse. However, no internet transmission or storage
          system can be guaranteed to be completely secure, so you use the
          website at your own risk.
        </p>
      </PolicySection>

      <PolicySection title="9. Your Choices and Rights">
        <PolicyList
          items={[
            "You may request correction of inaccurate contact or order information.",
            "You may opt out of non-essential promotional communication by contacting us.",
            "You may request details related to your data or raise a privacy concern by writing to our support team.",
            "We may ask for reasonable identity verification before processing privacy or account-related requests.",
          ]}
        />
      </PolicySection>

      <PolicySection title="10. Third-Party Links and Services">
        <p>
          Our website may contain links to third-party websites, courier tools,
          maps, or payment interfaces. Their privacy practices are governed by
          their own policies. We recommend reviewing those policies before
          sharing information on third-party platforms.
        </p>
      </PolicySection>

      <PolicySection title="11. Children's Privacy">
        <p>
          Our website is intended for general users and commercial customers. We
          do not knowingly collect personal information from children without
          appropriate authorisation from a parent or guardian.
        </p>
      </PolicySection>

      <PolicySection title="12. Contact for Privacy Queries">
        <p>
          For privacy questions, data correction requests, data-related
          complaints or payment-data clarification, contact:
        </p>
        <PolicyList
          items={[
            `${COMPANY_DETAILS.name}`,
            `Email: ${COMPANY_DETAILS.email}`,
            `Phone: ${COMPANY_DETAILS.phone}`,
            `Address: ${COMPANY_DETAILS.address}`,
            `Support hours: ${COMPANY_DETAILS.supportHours}`,
          ]}
        />
      </PolicySection>
    </PolicyPageLayout>
  );
};

export default PrivacyPolicy;
